import {
  PropsWithChildren,
  RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import css from './styles.module.css';

export type DirectionValues =
  | number
  | Partial<Record<'top' | 'right' | 'bottom' | 'left', number>>;

function getDirectionValues(value: DirectionValues) {
  if (typeof value === 'number') {
    return {
      top: value,
      right: value,
      bottom: value,
      left: value,
    };
  } else {
    return {
      top: value.top ?? 0,
      right: value.right ?? 0,
      bottom: value.bottom ?? 0,
      left: value.left ?? 0,
    };
  }
}

export interface TooltipPortalProps extends PropsWithChildren {
  containerRef: RefObject<HTMLElement | SVGElement>;
  left: number;
  top: number;
  collisionPadding?: DirectionValues;
  sideOffset?: DirectionValues;
}

export function TooltipPortal({
  children,
  containerRef,
  left: cursorLeft,
  top: cursorTop,
  collisionPadding = 10,
  sideOffset = 8,
}: TooltipPortalProps) {
  const offset = useMemo(() => getDirectionValues(sideOffset), [sideOffset]);
  const padding = useMemo(
    () => getDirectionValues(collisionPadding),
    [collisionPadding]
  );

  // Update the container position on scroll and resize
  const containerRect = useRef({ top: 0, left: 0 });
  useLayoutEffect(() => {
    function update() {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      containerRect.current.top = rect.top;
      containerRect.current.left = rect.left;
    }

    update();
    window.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [containerRef]);

  // Update tooltip position based on container, content, and cursor
  const positionerRef = useRef<HTMLDivElement>(null);
  const updatePosition = useCallback(() => {
    const positioner = positionerRef.current;
    if (!positioner) return;

    const containerTop = containerRect.current.top;
    const containerLeft = containerRect.current.left;

    const contentWidth = positioner.clientWidth;
    const contentHeight = positioner.clientHeight;

    const top = containerTop + cursorTop;
    const left = containerLeft + cursorLeft;

    // Keep x within min/max bounds, preferring right side
    const minX = padding.left;
    const maxX = window.innerWidth - padding.right - contentWidth;
    let x = left + offset.right;
    if (x > maxX) {
      x = left - contentWidth - offset.left;
    }
    x = Math.max(minX, Math.min(x, maxX));

    // Keep y within min/max bounds, preferring top side
    const minY = padding.top;
    const maxY = window.innerHeight - padding.bottom - contentHeight;
    let y = top - contentHeight - offset.top;
    if (y < minY) {
      y = top + offset.bottom;
    }
    y = Math.max(minY, Math.min(y, maxY));

    // Update position with translate3d for better performance
    positioner.style.transform = `translate3d(${x}px, ${y}px, 0px)`;
  }, [cursorLeft, cursorTop, padding, offset]);

  // Update position on mount and on animation frame
  useLayoutEffect(() => {
    let rafid: number | null = null;
    function callback() {
      updatePosition();
      rafid = requestAnimationFrame(callback);
    }
    callback();

    return () => {
      if (rafid) cancelAnimationFrame(rafid);
    };
  }, [updatePosition]);

  return createPortal(
    <div className={css.tooltipPortal}>
      <div ref={positionerRef}>{children}</div>
    </div>,
    document.body
  );
}
